import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import * as Sentry from '@sentry/react';

const getCSRFToken = (): string | null => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf_token') {
      return value;
    }
  }

  const token = Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');

  document.cookie = `csrf_token=${token}; path=/; SameSite=Strict`;
  return token;
};

export const axiosBaseQuery = (
  { baseUrl }: { baseUrl: string } = { baseUrl: '' }
): BaseQueryFn => async (args, api, extraOptions) => {
  const { url, method = 'GET', data, params, headers } = args;

  try {
    const apiToken = import.meta.env.VITE_API_TOKEN;
    const token = (api.getState() as any).auth?.token ? (api.getState() as any).auth?.token : apiToken;

    console.log(`🌐 API Request: ${method} ${baseUrl}${url}`);

    const isFormData = data instanceof FormData;
    const requestConfig: AxiosRequestConfig = {
      url: baseUrl + url,
      method,
      data,
      params,
      headers: {
        ...headers,
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(localStorage.getItem('seat-selection-session') && {
          'X-Session-Id': localStorage.getItem('seat-selection-session') || ''
        }),
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        ...(isFormData && { 'Content-Type': undefined }),
      },
      withCredentials: true,
    };

    console.log('📤 Request headers:', requestConfig.headers);
    const result = await axios(requestConfig);

    console.log(`✅ API Success: ${method} ${url}`, result.data);
    return { data: result.data };
  } catch (axiosError) {
    const err = axiosError as AxiosError;
    console.error(`❌ API Error: ${method} ${url}`, {
      status: err.response?.status,
      data: err.response?.data,
      message: err.message,
    });

    Sentry.captureException(err);

    return {
      error: {
        status: err.response?.status,
        data: err.response?.data || err.message,
        message: err.message,
      },
    };
  }
};
